import Service                                     from '@/api/service';
import { getKeyValueTrad, centToEuro, euroToCent } from '@/composables/formatData';

export function useFormatPartner(partner) {

  return {
    id    : partner.id,
    name  : partner.name,
    types : partner.types.map(type => getKeyValueTrad('partners.types', type))
  };
}

export function useUnformatPartner(newPartner, oldPartner) {

  const fieldsUpdated = {};

  for (const field of Object.keys(newPartner)) {

    switch (field) {
      case 'name' :
        if (JSON.stringify(newPartner[field]) !== JSON.stringify(oldPartner[field])) {
          fieldsUpdated[field] = newPartner[field];
        }

        break;

      case 'types' :
        if (JSON.stringify(newPartner[field]) !== JSON.stringify(oldPartner[field])) {
          fieldsUpdated.types = newPartner.types.map(type => type.key);
        }

        break;
    }
  }

  return fieldsUpdated;
}

export function useFormatPartnerScale(scale) {

  return {
    id               : scale.id,
    partnerId        : scale.partnerId,
    label            : scale.label,
    description      : scale.description,
    type             : getKeyValueTrad('partners.types', scale.type),
    calculationBasis : getKeyValueTrad('calculationBasis.types', scale.calculationBasis),
    levels           : useFormatPartnerScaleLevels(scale.levels),
    from             : scale.from,
    to               : scale.to,
    expandable       : true
  };
}

function useFormatPartnerScaleLevels(levels) {

  return levels.map(level => {

    return {
      ...level,
      threshold : centToEuro(level.threshold)
    };
  });
}

export function useUnformatPartnerScale(newScale, oldScale) {

  const fieldsUpdated = {};

  for (const field of Object.keys(newScale)) {

    switch (field) {
      case 'label' :
      case 'description' :
      case 'type':
        if (JSON.stringify(newScale[field]) !== JSON.stringify(oldScale[field])) {
          fieldsUpdated[field] = newScale[field];
        }

        break;

      case 'levels' :
        if (JSON.stringify(newScale[field]) !== JSON.stringify(oldScale[field])) {
          fieldsUpdated[field] = newScale.levels.map(level => {

            return {
              ...level,
              threshold : euroToCent(level.threshold)
            };
          });
        }

        break;

      case 'calculationBasis' :
        if (JSON.stringify(newScale[field]) !== JSON.stringify(oldScale[field])) {
          fieldsUpdated[field] = newScale[field].key;
        }

        break;
    }
  }

  // Update from only if it's an update of partner scale
  if (Object.keys(fieldsUpdated).length > 0 && oldScale?.id) {

    fieldsUpdated.from = newScale.from;
  }

  return fieldsUpdated;
}

export function usePartnerPercentToAmount(model, percent, folder) {

  const diagnosticReimbursementAmount = folder.diagnosticReimbursement.value || 0;

  switch (model) {
    case 'buyerPartnerLead' :

      return amountWithTwoDigits((folder.feesHT - folder.clientReimbursement - diagnosticReimbursementAmount) * percent / 100);

    case 'mandatePartnerLead' :

      let buyerPartnerLeadAmount = 0;
      if (folder.buyerPartnerLead.amount) {
        buyerPartnerLeadAmount = folder.buyerPartnerLead.amount.percent ? usePartnerPercentToAmount('buyerPartnerLead', folder.buyerPartnerLead.amount.value, folder) : folder.buyerPartnerLead.amount.value;
      }

      return amountWithTwoDigits(
        (folder.feesHT - folder.clientReimbursement - diagnosticReimbursementAmount - buyerPartnerLeadAmount)
        * percent / 100);
  }

  return null;
}

function amountWithTwoDigits(amount) {

  return Math.round(amount * 100) / 100;
}

/**
 * Return folder with partner lead amount calculated
 * Recalculate partner lead amount if one of these properties changed : fees, diagnostic amount, buyerPartnerLead source
 * or mandatePartnerLead source
 */
export async function useCalculatePartnersAmounts(tenant, newFolder, oldFolder) {

  const feesChanged = newFolder.fees !== oldFolder.fees;
  const clientReimbursementChanged = newFolder.clientReimbursement !== oldFolder.clientReimbursement.value;
  const diagnosticReimbursementChanged = newFolder.diagnosticReimbursement.value !== oldFolder.diagnosticReimbursement.value;
  const buyerPartnerLeadSourceChanged = newFolder.buyerPartnerLead.source?.value?.key !== oldFolder.buyerPartnerLead.source?.value?.key;
  const mandatePartnerLeadSourceChanged = newFolder.mandatePartnerLead.source?.value?.key !== oldFolder.mandatePartnerLead.source?.value?.key;

  if (!feesChanged && !diagnosticReimbursementChanged &&
    !buyerPartnerLeadSourceChanged && !mandatePartnerLeadSourceChanged && !clientReimbursementChanged) {

    return newFolder;
  }

  newFolder.buyerPartnerLead.amount = await getPartnerLeadAmount(tenant, 'buyerPartnerLead', buyerPartnerLeadSourceChanged, oldFolder, newFolder);
  newFolder.mandatePartnerLead.amount = await getPartnerLeadAmount(tenant, 'mandatePartnerLead', mandatePartnerLeadSourceChanged, oldFolder, newFolder);

  return newFolder;
}

/**
 * Return partner lead percentage :
 * If the source has changed, get back its percentage (0 if source has been remove)
 * If the source hasn't changed, get back old percentage (0 if amount was empty)
 */
export async function getPartnerLeadAmount(tenant, partnerLeadType, partnerLeadChanged, oldFolder, newFolder, overriddenFees = undefined) {

  // Partner changed but source or value has been deleted or source is none
  if (partnerLeadChanged &&
    (!newFolder[partnerLeadType].source.type || !newFolder[partnerLeadType].source.value || newFolder[partnerLeadType].source.type === 'none')
  ) {

    return { value : 0, percent : false };

    // Partner changed and source is internal : get back amount from API
  } else if (partnerLeadChanged && newFolder[partnerLeadType].source.type.key === 'internal') {

    const payload = {
      type      : partnerLeadType === 'mandatePartnerLead' ? 'mandate' : 'buyer',
      dossierId : oldFolder.id,
      date      : new Date()
    };
    if (overriddenFees !== undefined) {

      payload.overriddenFees = euroToCent(overriddenFees);
    }

    const partnerAppliedLevel = await Service.getAppliedLevel(tenant, newFolder[partnerLeadType].source.value.key, payload);

    if (partnerAppliedLevel.appliedLevel?.percent) {

      return { value : partnerAppliedLevel.appliedLevel.value, percent : true };
    }

    return { value : centToEuro(partnerAppliedLevel.appliedLevel?.value || partnerAppliedLevel.amount), percent : false };

    // Partner changed and source is ada : get back percent from API and convert to amount
  } else if (partnerLeadChanged && newFolder[partnerLeadType].source.type.key === 'ada') {

    const ada = await Service.getAda(tenant, newFolder[partnerLeadType].source.value.key);
    return partnerLeadType.includes('buyer') ? { value : ada.buyerCommissionPercentage, percent : true } : { value : ada.sellerCommissionPercentage, percent : true };

    // Partner hasn't changed and there is an old amount
  } else if (!partnerLeadChanged && oldFolder[partnerLeadType].amount) {

    return oldFolder[partnerLeadType].amount;
  }

  return 0;
}
